import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, Search, X } from "lucide-react";

const SIZES = {
  xs: { btn: "px-2.5 py-1.5 rounded-md text-[11px]", opt: "px-2.5 py-1 text-[11px]", box: "w-3 h-3", chev: "w-3 h-3" },
  sm: { btn: "px-3 py-1.5 rounded-lg text-xs", opt: "px-3 py-1.5 text-xs", box: "w-3.5 h-3.5", chev: "w-3 h-3" },
  md: { btn: "px-3 py-2 rounded-lg text-[13px]", opt: "px-3 py-1.5 text-sm", box: "w-4 h-4", chev: "w-3.5 h-3.5" },
  lg: { btn: "px-3 py-2 rounded-xl text-sm", opt: "px-3 py-2 text-sm", box: "w-4 h-4", chev: "w-4 h-4" },
  xl: { btn: "px-3 py-2.5 rounded-xl text-sm", opt: "px-3 py-2 text-sm", box: "w-4 h-4", chev: "w-4 h-4" },
};

const normOpt = (o) => {
  if (o == null) return null;
  if (typeof o === "object") {
    const v = o.value ?? o.v ?? o.id ?? o.key;
    return { value: v, label: String(o.label ?? o.l ?? o.name ?? v) };
  }
  return { value: o, label: String(o) };
};

/**
 * FilterMultiSelect — dropdown unifié : recherche, sélection multiple, "Tout sélectionner".
 * options : string[] | {value,label}[] | {v,l}[] ; labelMap optionnel (value → label)
 * value   : any[] (multi) ; single=true → onChange([v]) et fermeture
 */
export default function FilterMultiSelect({
  label, options = [], value, values, onChange, labelMap,
  single = false, placeholder, size = "sm", minWidth, className = "",
  topLabel = false, topLabelClass = "text-[10px] font-semibold text-slate-500 mb-1",
  activeStyle = true, menuWidth = 260, disabled = false, emptyText = "Aucune option", countLabel = false,
  "data-testid": dataTestId, testid: _tid,
}) {
  const testid = _tid || dataTestId;
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);
  const inputRef = useRef(null);
  const S = SIZES[size] || SIZES.sm;
  const sel = value ?? values ?? [];

  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    const t = setTimeout(() => inputRef.current?.focus(), 10);
    return () => { document.removeEventListener("mousedown", h); clearTimeout(t); };
  }, [open]);
  useEffect(() => { if (!open) setQ(""); }, [open]);

  const opts = useMemo(() => options.map(normOpt).filter(o => o && o.value !== "" && o.value != null)
    .map(o => ({ ...o, label: labelMap?.[o.value] ?? o.label })), [options, labelMap]);
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? opts.filter(o => o.label.toLowerCase().includes(s) || String(o.value).toLowerCase().includes(s)) : opts;
  }, [opts, q]);

  const labelOf = (v) => opts.find(o => o.value === v)?.label ?? String(v);
  const allText = placeholder || (label ? `Tous · ${label}` : "Tous");
  const summary = sel.length === 0 ? allText : countLabel && label ? `${label} (${sel.length})` : sel.length === 1 ? labelOf(sel[0]) : `${sel.length} sélectionnés`;

  const toggle = (v) => {
    if (single) { onChange([v]); setOpen(false); return; }
    onChange(sel.includes(v) ? sel.filter(x => x !== v) : [...sel, v]);
  };
  const allFilteredSelected = filtered.length > 0 && filtered.every(o => sel.includes(o.value));
  const selectAll = () => {
    if (allFilteredSelected) onChange(sel.filter(v => !filtered.some(o => o.value === v)));
    else onChange([...new Set([...sel, ...filtered.map(o => o.value)])]);
  };
  const clear = (e) => { e?.stopPropagation(); onChange([]); };

  const style = typeof minWidth === "number" ? { minWidth } : undefined;
  const mwClass = typeof minWidth === "string" ? minWidth : "";
  const active = activeStyle && sel.length > 0;

  return (
    <div className={`relative ${mwClass} ${className}`} ref={ref} style={style} data-testid={testid ? `${testid}-wrap` : undefined}>
      {topLabel && label && <label className={`block ${topLabelClass}`}>{label}</label>}
      <button type="button" data-testid={testid} disabled={disabled} onClick={() => setOpen(o => !o)}
              className={`w-full flex items-center gap-1.5 border font-medium transition-colors ${S.btn}
                ${active ? "bg-blue-50 border-blue-300 text-blue-800" : "bg-white border-slate-200 text-slate-700 hover:border-slate-300"}
                ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}>
        <span className={`flex-1 text-left truncate ${sel.length ? "" : "text-slate-500 font-normal"}`}>{summary}</span>
        {sel.length > 0 && !disabled && (
          <span role="button" onClick={clear} data-testid={testid ? `${testid}-clear` : undefined}
                className="w-4 h-4 grid place-items-center rounded hover:bg-blue-100">
            <X className="w-3 h-3 text-slate-400"/>
          </span>
        )}
        <ChevronDown className={`${S.chev} text-slate-400 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}/>
      </button>
      {open && (
        <div className="absolute z-50 mt-1 min-w-full bg-white border border-slate-200 rounded-lg shadow-xl overflow-hidden"
             style={{ width: menuWidth }} data-testid={testid ? `${testid}-menu` : undefined}>
          <div className="p-1.5 border-b border-slate-100">
            <div className="relative">
              <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400"/>
              <input ref={inputRef} value={q} onChange={e => setQ(e.target.value)} placeholder="Rechercher…"
                     data-testid={testid ? `${testid}-search` : undefined}
                     className="w-full pl-6 pr-2 py-1 rounded-md text-xs border border-slate-200 outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400"/>
            </div>
          </div>
          <div className="flex items-center justify-between px-3 py-1.5 border-b border-slate-100 bg-slate-50 text-[10px]">
            {!single ? (
              <button type="button" onClick={selectAll} data-testid={testid ? `${testid}-all` : undefined}
                      className="font-semibold text-blue-700 hover:underline">
                {allFilteredSelected ? "Tout désélectionner" : "Tout sélectionner"}{q ? ` (${filtered.length})` : ""}
              </button>
            ) : <span className="font-semibold uppercase tracking-wider text-slate-500">{label || ""} ({opts.length})</span>}
            {sel.length > 0 && <button type="button" onClick={clear} className="font-semibold text-rose-600 hover:underline">Effacer</button>}
          </div>
          <div className="max-h-[260px] overflow-y-auto scrollbar-thin">
            {filtered.length === 0 && <div className="px-3 py-2 text-xs italic text-slate-400">{q ? "Aucun résultat" : emptyText}</div>}
            {filtered.map(o => {
              const on = sel.includes(o.value);
              return (
                <button key={String(o.value)} type="button" onClick={() => toggle(o.value)}
                        data-testid={testid ? `${testid}-opt-${o.value}` : undefined}
                        className={`w-full flex items-center gap-2 text-left ${S.opt} ${on ? "bg-blue-50 text-blue-800 font-semibold" : "text-slate-700 hover:bg-slate-50"}`}>
                  {!single && (
                    <span className={`${S.box} rounded border grid place-items-center shrink-0 ${on ? "bg-blue-600 border-blue-600" : "border-slate-300 bg-white"}`}>
                      {on && <Check className="w-2.5 h-2.5 text-white"/>}
                    </span>
                  )}
                  <span className="truncate">{o.label}</span>
                  {single && on && <Check className="w-3 h-3 text-blue-600 ml-auto"/>}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/** Variante mono-valeur (formulaires) : value string ↔ onChange(string) */
export function SearchSelect({ value, onChange, options, placeholder = "Sélectionner…", allowEmpty = true, ...rest }) {
  return (
    <FilterMultiSelect {...rest} single options={options} placeholder={placeholder}
                       value={value != null && value !== "" ? [value] : []}
                       activeStyle={false}
                       onChange={(arr) => onChange(arr[0] ?? "")}/>
  );
}
